import { cn } from "@/lib/utils";
import { HTMLAttributes, useEffect, useRef } from "react";

import { Tab } from "./tabs/Tab";

export function HorizontalPicker<T>({
    className,
    items,
    value,
    onChange,
    ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "onChange"> & {
    className?: string;
    items: Array<{ label: string; value: T }>;
    value: T;
    onChange: (value: T) => void;
}) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            const children = Array.from(ref.current.children);

            const index = items.findIndex((item) => item.value === value);

            if (index === -1) {
                return;
            }

            const target = children[index];

            const isMobile = ref.current.clientWidth <= 768;

            if (target instanceof HTMLElement) {
                ref.current.scrollTo({
                    left: target.offsetLeft - (isMobile ? 16 : 24),
                    behavior: "smooth"
                });
            }
        }
    }, [value, items]);

    return (
        <div
            className={cn(
                "relative mx-[calc(-1*var(--page-padding))] bg-background",
                className
            )}
            {...props}
        >
            <div
                ref={ref}
                className="flex items-center overflow-auto bg-background px-[var(--page-padding)]"
            >
                {items.map((item) => (
                    <Tab
                        className="flex-shrink-0 [&:not(:last-of-type)]:mr-1"
                        key={item.label}
                        variant="filled"
                        active={item.value === value}
                        onClick={() => {
                            onChange(item.value);
                        }}
                    >
                        {item.label}
                    </Tab>
                ))}
            </div>
        </div>
    );
}
